import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { ElementLayoutResult, Viewer3DContent, WatchedElementResult } from '../3d-viewer/viewer-3d-content'
import { autoLayoutBasket3D, autoLayoutBasket3DWithPrevious, basket3DActions } from './basket-3d-slice';
import { AppDispatch, RootState } from './store';
import { MAIN_SCENE } from './types/composition';
import { EventEmitter, eventTypes, events } from './event-emitter';
import { mainActions } from './main-slice';

export type ViewerState = {
    content?: Viewer3DContent
    elementsLayout: Record<string, ElementLayoutResult>
    elementDimensions: Record<string, WatchedElementResult>
    loading: boolean
}

const initialState: ViewerState = {
    elementsLayout: {},
    elementDimensions: {},
    loading: false
}

export const viewerSlice = createSlice({
    name: 'viewer',
    initialState,
    reducers: {
        setContent(state, action: PayloadAction<Viewer3DContent|undefined>) {
            state.content = action.payload;
        },
        _setLayoutResults: (state, action: PayloadAction<{
            layout: Record<string, ElementLayoutResult>,
            dimensions: Record<string, WatchedElementResult>
        }>) => {
            state.elementsLayout = action.payload.layout;
            state.elementDimensions = action.payload.dimensions;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        }
    },
    extraReducers: builder => {
        builder.addCase(mainActions.enterPage, (state, action) => {
            if(action.payload !== 'configurator' && action.payload !== 'cart3d') {
                state.content = undefined;
                state.loading = false;
                state.elementsLayout = {};
                state.elementDimensions = {};
            }
        })
        .addCase(basket3DActions.startLoading, (state) => {
            state.content = undefined;
            state.loading = true;
            state.elementsLayout = {};
            state.elementDimensions = {};
        })
        .addCase(basket3DActions.loadCart3D.fulfilled, (state, action) => {
            if(action.payload) {
                state.content = action.payload.viewerContent;
            } else {
                state.content = undefined;
            }
            state.elementsLayout = {};
            state.elementDimensions = {};
            state.loading = false;
    })
    }
});

const setLayoutResults = (results: {
    layout: Record<string, ElementLayoutResult>,
    dimensions: Record<string, WatchedElementResult>
}) => {
    return (dispatch: AppDispatch, getState: () => RootState) => {
        dispatch(viewerActions._setLayoutResults(results));
        const state = getState();

        if(state.basket3D.isSuccess && !state.basket3D.layedOut) {

            const dimensions = results.dimensions;
            // check if all the nodes have info
            let allOK = true;
            for(const node of state.basket3D.rootNodes) {
                if(!dimensions[node]) {
                    allOK = false;
                } else if(dimensions[node].dimensions[0] === 0 && dimensions[node].dimensions[1] === 0) {
                    allOK = false;
                }
            }

            if(allOK && state.viewer.content) {

                const previousLayout = state.basket3D.temporaryLoadLayout;
                let autoLayoutResult;

                if(previousLayout.length === 0) {
                    autoLayoutResult = autoLayoutBasket3D(
                        state.basket3D.rootNodes, 
                        state.viewer.elementDimensions,
                        state.viewer.content
                    );    
                } else {
                    autoLayoutResult = autoLayoutBasket3DWithPrevious(
                        state.basket3D.rootNodes, 
                        state.viewer.elementDimensions,
                        state.viewer.content,
                        state.basket3D.idReferences,
                        previousLayout
                    )
                }

                dispatch(basket3DActions.setAutoLayout(autoLayoutResult));
                setTimeout(() => {
                    EventEmitter.dispatch(eventTypes.VIEW_CONTROLS, events.FIT_CONTENT_EVENT, MAIN_SCENE);
                }, 200)
            }
    
        }
    }
}

export const viewerActions = {
    ...viewerSlice.actions,
    setLayoutResults
}

export const viewerContentSelector = (state: RootState) => state.viewer.content;
export const viewerLoadingSelector = (state: RootState) => state.viewer.loading;

export default viewerSlice.reducer;
